import React from 'react';
import { Button, Spinner, Typography } from '@material-tailwind/react';
import { ArrowLeftIcon, HistoryIcon, PencilIcon } from 'lucide-react';
import moment from 'moment';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetFloorById } from '../../custom-hooks/use-floors';
import { useGetFloorLayout } from '../../custom-hooks/use-floor-layouts';
import { useAuth } from '../../services/contexts/authContext';
import { canEditFloorPlan } from '../../components/floor-plan/constants';
import {
  managementFloorEditUrl,
  managementFloorUrl,
} from './urls';

const ManagementFloorPlanHistoryPage = () => {
  const { branchId, buildingId, floorId } = useParams();
  const navigate = useNavigate();
  const { userDetails } = useAuth();
  const canEdit = canEditFloorPlan(userDetails?.role);

  const { data: floorRes, isLoading: floorLoading } = useGetFloorById(floorId);
  const floor = floorRes?.data?.floor;
  const { data: layoutPayload, isLoading: layoutLoading } = useGetFloorLayout(floorId);
  const layout = layoutPayload?.layout;

  const goBack = () => {
    navigate(managementFloorUrl(branchId, buildingId, floorId));
  };

  if (floorLoading || layoutLoading) {
    return (
      <div className="m-5 p-5 border rounded-3xl flex justify-center py-16">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  const draftCount = layout?.shapes?.length ?? 0;
  const publishedCount = layout?.publishedShapes?.length ?? 0;

  return (
    <div className="m-5 p-5 border rounded-3xl">
      <Button
        variant="text"
        size="sm"
        className="rounded-full flex items-center gap-2 mb-4 -ml-2"
        onClick={goBack}
      >
        <ArrowLeftIcon size={16} />
        Back to floor plan
      </Button>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-6">
        <div>
          <h1 className="md:text-2xl font-thin flex items-center gap-2">
            <HistoryIcon size={22} className="text-gray-400" />
            Floor plan history
          </h1>
          <Typography variant="small" className="text-gray-500 mt-1">
            Floor {floor?.number}
            {floor?.floorCode ? ` · ${floor.floorCode}` : ''}
            {floor?.building?.name ? ` · ${floor.building.name}` : ''}
          </Typography>
        </div>
        {canEdit && (
          <Button
            size="sm"
            className="rounded-full flex items-center gap-2 normal-case font-normal"
            onClick={() => navigate(managementFloorEditUrl(branchId, buildingId, floorId))}
          >
            <PencilIcon size={14} />
            Edit layout
          </Button>
        )}
      </div>

      {!layout ? (
        <div className="rounded-2xl border border-dashed p-10 text-center text-gray-500">
          No layout has been created for this floor yet.
          {canEdit && (
            <div className="mt-4">
              <Button
                size="sm"
                className="rounded-full normal-case"
                onClick={() => navigate(managementFloorEditUrl(branchId, buildingId, floorId))}
              >
                Create layout
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-2xl border p-4">
              <Typography variant="small" className="text-gray-500 uppercase tracking-wide text-[11px]">
                Current status
              </Typography>
              <Typography variant="h6" className="capitalize mt-1">
                {layout.publishStatus || 'draft'}
              </Typography>
              {layout.publishedAt && (
                <Typography variant="small" className="text-gray-500 mt-2">
                  Last published {moment(layout.publishedAt).format('MMM D, YYYY · h:mm A')}
                </Typography>
              )}
            </div>
            <div className="rounded-2xl border p-4">
              <Typography variant="small" className="text-gray-500 uppercase tracking-wide text-[11px]">
                Shapes
              </Typography>
              <Typography variant="h6" className="mt-1">
                {draftCount} draft · {publishedCount} published
              </Typography>
            </div>
          </div>

          <div className="rounded-2xl border overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b">
              <Typography variant="small" className="font-medium text-gray-700">
                Version timeline
              </Typography>
            </div>
            <ul className="divide-y">
              {layout.publishedAt && (
                <li className="px-4 py-3 flex items-start justify-between gap-4">
                  <div>
                    <div className="font-medium text-sm">Published snapshot</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {publishedCount} shapes on the live floor plan
                    </div>
                  </div>
                  <div className="text-xs text-gray-400 shrink-0">
                    {moment(layout.publishedAt).format('MMM D, YYYY')}
                  </div>
                </li>
              )}
              {layout.lastUpdated && (
                <li className="px-4 py-3 flex items-start justify-between gap-4">
                  <div>
                    <div className="font-medium text-sm">Draft updated</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {draftCount} shapes in the working draft
                    </div>
                  </div>
                  <div className="text-xs text-gray-400 shrink-0">
                    {moment(layout.lastUpdated).format('MMM D, YYYY')}
                  </div>
                </li>
              )}
              {layout.createdDate && (
                <li className="px-4 py-3 flex items-start justify-between gap-4">
                  <div>
                    <div className="font-medium text-sm">Layout created</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Initial floor plan layout
                    </div>
                  </div>
                  <div className="text-xs text-gray-400 shrink-0">
                    {moment(layout.createdDate).format('MMM D, YYYY')}
                  </div>
                </li>
              )}
            </ul>
          </div>

          {layout.publishStatus === 'draft' && layout.publishedAt && (
            <div className="rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
              The draft has unpublished changes. Publish from the editor to update the live floor plan.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ManagementFloorPlanHistoryPage;
